import React from 'react';
import './Features.css';

interface Feature {
  icon: string;
  title: string;
  description: string;
  benefits: string[];
}

const Features: React.FC = () => {
  const features: Feature[] = [
    {
      icon: '👨‍👩‍👧‍👦',
      title: 'Parent Control Dashboard',
      description: 'Complete household oversight with real-time insights into chore availability, completion status, and payment management across all children.',
      benefits: ['View available and pending chores', 'Track total payouts across all children', 'Approve or reject completed tasks', 'Force unclaim chores when needed']
    },
    {
      icon: '🎯',
      title: 'Smart Chore Management',
      description: 'Create and manage chores with flexible deadlines and payment structures that adapt to your family\'s needs.',
      benefits: ['Set custom chore deadlines', 'Manage household accounts', 'Control chore availability', 'Track completion progress']
    },
    {
      icon: '💰',
      title: 'Kids Earning Dashboard',
      description: 'Empower children with their own dashboard to track earnings, claim available tasks, and submit completed work for review.',
      benefits: ['Personal earnings tracking', 'Self-service task claiming', 'Submit work for parent review', 'Real-time payout notifications']
    },
    {
      icon: '✅',
      title: 'Review & Approval System',
      description: 'Streamlined workflow where kids submit completed chores for parent review, ensuring quality and building accountability.',
      benefits: ['Parent approval workflow', 'Quality control checkpoints', 'Teaching moment opportunities', 'Fair payment processing']
    },
    {
      icon: '🔔',
      title: 'Intelligent Alert System',
      description: 'Stay connected with real-time notifications for every step of the chore lifecycle, keeping the whole family informed.',
      benefits: ['New chore availability alerts', 'Task claiming notifications', 'Review ready alerts', 'Payout confirmations']
    },
    {
      icon: '📊',
      title: 'Household Analytics',
      description: 'Comprehensive insights into your family\'s chore completion patterns, earnings, and productivity trends.',
      benefits: ['Family-wide completion rates', 'Payment history tracking', 'Performance insights', 'Productivity analytics']
    }
  ];

  return (
    <section id="features" className="features section" role="main" aria-labelledby="features-heading">
      <div className="container">
        <header className="features-header">
          <h2 id="features-heading">Why Families Love ChoreTap</h2>
          <p className="features-subtitle">
            Discover how ChoreTap transforms household management into an engaging family experience
            that teaches responsibility, builds life skills, and brings families closer together.
          </p>
        </header>
        
        <div className="features-grid grid grid-3" role="list" aria-label="ChoreTap features">
          {features.map((feature, index) => (
            <article key={index} className="feature-card" role="listitem">
              <div className="feature-icon" aria-hidden="true">{feature.icon}</div>
              <h3 className="feature-title">{feature.title}</h3>
              <p className="feature-description">{feature.description}</p>
              <ul className="feature-benefits" aria-label={`${feature.title} benefits`}>
                {feature.benefits.map((benefit, benefitIndex) => (
                  <li key={benefitIndex} className="feature-benefit">
                    <span className="benefit-icon" aria-hidden="true">✓</span>
                    {benefit}
                  </li>
                ))}
              </ul>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Features;
